import React, { useState, useContext } from 'react';
import './object.css';
import { validateSteps } from "../validateSteps";
import { LanguageContext } from "../context/LanguageContext";

const CreateObjectStep6 = ({
  formData,
  setFormData,
  onNext,
  onBack,
  missingFields = [],
  setMissingFields,
  validationActive
}) => {

  const { texts } = useContext(LanguageContext);

  const MIN_WIDTH = 800;
  const MIN_HEIGHT = 600;

  const [isDraggingTitle, setIsDraggingTitle] = useState(false);
  const [isDraggingAdditional, setIsDraggingAdditional] = useState(false);

  // ------------------------------------------------------------
  // Bildgröße prüfen
  // ------------------------------------------------------------
  const isValidImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectURL = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width >= MIN_WIDTH && img.height >= MIN_HEIGHT) {
          resolve(true);
        } else {
          reject(texts.object.step6.errorMinSize.replace("{w}", MIN_WIDTH).replace("{h}", MIN_HEIGHT));
        }
        URL.revokeObjectURL(objectURL);
      };
      img.onerror = () => {
        reject(texts.object.step6.errorInvalidFormat);
        URL.revokeObjectURL(objectURL);
      };
      img.src = objectURL;
    });
  };

  // ------------------------------------------------------------
  // Bild hochladen
  // ------------------------------------------------------------
  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("image", file);

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-image`, {
      method: "POST",
      body: fd
    });

    const data = await res.json();
    return data.path;
  };

  // ------------------------------------------------------------
  // Titelbild hochladen
  // ------------------------------------------------------------
  const handleTitleImageUpload = (file) => {
    if (!file) return;

    isValidImage(file)
      .then(async () => {
        const uploadedPath = await uploadImage(file);

        const updated = { ...formData, title_image: uploadedPath };
        setFormData(updated);

        if (validationActive) {
          const { missing } = validateSteps(updated);
          setMissingFields(missing);
        }
      })
      .catch((error) => alert(error));
  };

  // ------------------------------------------------------------
  // Weitere Bilder hochladen
  // ------------------------------------------------------------
  const handleAdditionalImagesUpload = (files) => {
    const currentImages = formData.additional_images || [];
    const remainingSlots = 20 - currentImages.length;

    const selectedFiles = files.slice(0, remainingSlots);

    const validFilesPromises = selectedFiles.map((file) =>
      isValidImage(file)
        .then(() => file)
        .catch((error) => {
          alert(error);
          return null;
        })
    );

    Promise.all(validFilesPromises).then(async (results) => {
      const validFiles = results.filter(Boolean);

      if (validFiles.length > 0) {
        const uploadPromises = validFiles.map((file) => uploadImage(file));
        const uploadedPaths = await Promise.all(uploadPromises);

        const updated = {
          ...formData,
          additional_images: [...currentImages, ...uploadedPaths],
        };

        setFormData(updated);

        if (validationActive) {
          const { missing } = validateSteps(updated);
          setMissingFields(missing);
        }
      }
    });
  };

  // ------------------------------------------------------------
  // Drag & Drop Events
  // ------------------------------------------------------------
  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'title') setIsDraggingTitle(true);
    if (type === 'additional') setIsDraggingAdditional(true);
  };

  const handleDragLeave = (type) => {
    if (type === 'title') setIsDraggingTitle(false);
    if (type === 'additional') setIsDraggingAdditional(false);
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (type === 'title') {
      setIsDraggingTitle(false);
      if (files.length > 0) handleTitleImageUpload(files[0]);
    }
    if (type === 'additional') {
      setIsDraggingAdditional(false);
      handleAdditionalImagesUpload(files);
    }
  };

  // ------------------------------------------------------------
  // Bilder entfernen
  // ------------------------------------------------------------
  const removeTitleImage = () => {
    const updated = { ...formData, title_image: null };
    setFormData(updated);

    if (validationActive) {
      const { missing } = validateSteps(updated);
      setMissingFields(missing);
    }
  };

  const removeAdditionalImage = (index) => {
    const updatedImages = [...(formData.additional_images || [])];
    updatedImages.splice(index, 1);

    const updated = { ...formData, additional_images: updatedImages };
    setFormData(updated);

    if (validationActive) {
      const { missing } = validateSteps(updated);
      setMissingFields(missing);
    }
  };

  const titleImage = formData.title_image;
  const additionalImages = formData.additional_images || [];

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div className="object-form-container">
      <h2 className="object-header">{texts.object.step6.title}</h2>

      <div className="object-content-scroll-area">
        <div className="form-cards">

          <form className="form-group-attachment">

            {/* Titelbild */}
            <label htmlFor="title_image" className="object-title-image">
              {texts.object.step6.titleImage}
            </label>

            <div
              className={`dropzone ${isDraggingTitle ? 'drag-over' : ''} ${
                validationActive && missingFields.includes("title_image") ? "input-error" : ""
              }`}
              onDragOver={(e) => handleDragOver(e, 'title')}
              onDragLeave={() => handleDragLeave('title')}
              onDrop={(e) => handleDrop(e, 'title')}
            >
              <label htmlFor="title_image" className="custom-upload-button">
                {titleImage
                  ? texts.object.step6.uploaded
                  : texts.object.step6.selectOrDragTitle}
              </label>

              <input
                type="file"
                id="title_image"
                name="title_image"
                onChange={(e) => handleTitleImageUpload(e.target.files[0])}
                accept="image/*"
                style={{ display: 'none' }}
              />

              <div className="image-preview-container">
                {titleImage ? (
                  <div className="image-preview">
                    <img
                      src={`${process.env.REACT_APP_API_URL}${titleImage}`}
                      alt="Titelbild"
                    />
                    <button
                      type="button"
                      onClick={removeTitleImage}
                      className="remove-image"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="image-drop-placeholder">
                    {texts.object.step6.dragTitleHere}
                  </div>
                )}
              </div>
            </div>

            {/* Weitere Bilder */}
            <label htmlFor="additional_images" className="object-title">
              {texts.object.step6.additionalImages}
            </label>

            <div
              className={`dropzone ${isDraggingAdditional ? 'drag-over' : ''} ${
                validationActive && missingFields.includes("additional_images") ? "input-error" : ""
              }`}
              onDragOver={(e) => handleDragOver(e, 'additional')}
              onDragLeave={() => handleDragLeave('additional')}
              onDrop={(e) => handleDrop(e, 'additional')}
            >
              <label htmlFor="additional_images" className="custom-upload-button">
                {additionalImages.length > 0
                  ? texts.object.step6.uploadedCount.replace("{n}", additionalImages.length)
                  : texts.object.step6.selectOrDragAdditional}
              </label>

              <input
                type="file"
                id="additional_images"
                name="additional_images"
                multiple
                onChange={(e) => handleAdditionalImagesUpload(Array.from(e.target.files))}
                accept="image/*"
                style={{ display: 'none' }}
              />

              <div className="image-preview-container">
                {additionalImages.length > 0 ? (
                  additionalImages.map((image, index) => (
                    <div key={index} className="image-preview">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${image}`}
                        alt={`Bild ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        className="remove-image"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="image-drop-placeholder">
                    {texts.object.step6.dragAdditionalHere}
                  </div>
                )}
              </div>
            </div>

          </form>

        </div>
      </div>
    </div>
  );
};

export default CreateObjectStep6;
